import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { Div } from 'client/widget-components/basicComponents';
import Checkbox from 'client/widget-components/basicComponents/Checkbox/Checkbox';
import { countUnits } from 'client/widget-components/ecom/stores/checkboxValueUtils';
import { CheckboxChoice } from 'client/widget-components/ecom/stores/types';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { useMemo } from 'react';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';
import { ProductCustomizationsStyles } from '../../types';
import PaidAddonLabel from '../PaidAddonLabel';
import ChoiceQuantityStepper from './ChoiceQuantityStepper';

interface CheckboxCustomizationChoiceProps {
    choice: CheckboxChoice;
    value: string[];
    /** Max total units reached — disables `+` and the unselected checkbox. */
    limitReached?: boolean;
    /** Editor-preview only: render the choice as selected (no state change). */
    previewAllSelected?: boolean;
    paidAddonTextStyles?: ProductCustomizationsStyles['checkboxPaidAddonText'];
    onChange: (label: string, checked: boolean) => void;
    onQuantityChange: (label: string, delta: number) => void;
    styles?: ProductCustomizationsStyles;
}

const CheckboxCustomizationChoice = ({
    choice,
    value,
    limitReached,
    previewAllSelected,
    paidAddonTextStyles,
    onChange,
    onQuantityChange,
    styles,
}: CheckboxCustomizationChoiceProps) => {
    const realQuantity = useMemo(
        () => countUnits(value, choice.label),
        [value, choice.label]
    );
    const checked = previewAllSelected || realQuantity > 0;
    const quantity =
        realQuantity > 0 ? realQuantity : previewAllSelected ? 1 : 0;
    const showStepper = !!choice.quantity_enabled && checked;
    // Selected choices stay interactive at the limit — unchecking is how the
    // shopper frees up capacity again.
    const disabled = !!limitReached && !checked;

    return (
        <Div styles={choiceItemDefaultStyles}>
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(choice.label, e.target.checked)}
                dataGrab={CUSTOMIZATIONS_DATA_GRABS.choiceDataGrab}
                labelDataGrab={CUSTOMIZATIONS_DATA_GRABS.choiceLabelDataGrab}
                checkedDataGrab={
                    CUSTOMIZATIONS_DATA_GRABS.choiceCheckedDataGrab
                }
                dataAuto={`customization-choice-${choice.label}`}
                styles={{
                    checkboxRow: mergeResponsiveStyles([
                        checkboxRowDefaultStyles,
                        ...(disabled ? [disabledChoiceStyles] : []),
                    ]),
                    checkbox: styles?.choice,
                    checkboxChecked: styles?.choiceChecked,
                    checkboxLabel: styles?.choiceLabel,
                }}
            >
                <PaidAddonLabel
                    label={choice.label}
                    price={choice.price}
                    displayedPrice={choice.displayed_price}
                    styles={paidAddonTextStyles}
                />
            </Checkbox>
            {showStepper && (
                <ChoiceQuantityStepper
                    label={choice.label}
                    quantity={quantity}
                    disableIncrement={!!limitReached}
                    onIncrement={() => onQuantityChange(choice.label, 1)}
                    onDecrement={() => onQuantityChange(choice.label, -1)}
                    containerStyles={styles?.choiceQuantityStepper}
                    valueStyles={styles?.choiceLabel}
                    dataAuto={`customization-choice-quantity-${choice.label}`}
                />
            )}
        </Div>
    );
};

export default CheckboxCustomizationChoice;

const choiceItemDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
    },
};

const checkboxRowDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
    },
};

const disabledChoiceStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        cursor: 'not-allowed',
        opacity: 0.5,
    },
};
